import { ReactNode } from "react";
import { useFormContext } from "react-hook-form";

import { Edit } from "components/icons/generated";
import KeyValue from "components/KeyValue";
import Box from "ds/components/Box";
import ButtonIcon from "ds/components/ButtonIcon";
import CardCollapsible from "ds/components/Card/Collapsible";
import Counter from "ds/components/Counter";
import FullScreenModalBody from "ds/components/FullScreenModal/Body";
import FullScreenModalBodyHeader from "ds/components/FullScreenModal/BodyHeader";
import Tag from "ds/components/Tag";
import { useToggle } from "hooks/useToggle";
import useTypedContext from "hooks/useTypedContext";

import { SCHEDULE_SCAN_WIZARD_STEP_HEADERS } from "../constants";
import { NewScanScheduleContext } from "../context";
import { NewScanScheduleFormFields, NewScanScheduleStep } from "../types";

// Right-aligned chips, mirroring how the stack-creation summary renders list values.
const TagList = ({ values }: { values: string[] }) => (
  <Box gap="medium" justify="end" wrap>
    {values.map((value) => (
      <Tag key={value} tag={value} />
    ))}
  </Box>
);

// A summary section wrapped in a collapsible card (mirrors NewStack's summary Panel).
const SummaryPanel = ({
  title,
  step,
  count,
  children,
}: {
  title: string;
  step: NewScanScheduleStep;
  count?: number;
  children: ReactNode;
}) => {
  const { goToStep } = useTypedContext(NewScanScheduleContext);
  const [isCollapsed, toggle] = useToggle(false);

  return (
    <CardCollapsible
      fullWidth
      ariaLevel={3}
      ariaLabel={title}
      title={title}
      isCollapsed={isCollapsed}
      onToggle={toggle}
      titleSuffix={count !== undefined ? <Counter count={count} /> : undefined}
      headerSuffix={
        <ButtonIcon icon={Edit} variant="ghost" onPress={() => goToStep(step)}>
          Edit step
        </ButtonIcon>
      }
    >
      {children}
    </CardCollapsible>
  );
};

const NewScanScheduleStepSummary = () => {
  const { watch } = useFormContext<NewScanScheduleFormFields>();

  const formValues = watch();

  return (
    <FullScreenModalBody>
      <FullScreenModalBodyHeader
        {...SCHEDULE_SCAN_WIZARD_STEP_HEADERS[NewScanScheduleStep.Summary]}
      />

      <Box direction="column" gap="large">
        <SummaryPanel title="Scan details" step={NewScanScheduleStep.ScanDetails}>
          <KeyValue name="Name">{formValues.name || "–"}</KeyValue>
          <KeyValue name="Space">{formValues.space || "–"}</KeyValue>
          <KeyValue name="Labels">
            {formValues.labels.length > 0 ? (
              <TagList values={formValues.labels.map((label) => label.value)} />
            ) : (
              "–"
            )}
          </KeyValue>
          <KeyValue name="Description">{formValues.description || "–"}</KeyValue>
        </SummaryPanel>
      </Box>
    </FullScreenModalBody>
  );
};

export default NewScanScheduleStepSummary;
